import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { dateFormatterHourMinute } from "../../../format";
import { typographies } from "../../../styles/figmaTypographies";
import { Spacer } from "../Spacer";
import { getAddressLines } from "../../../geolocUtils";
import { TextCapitalized } from "../text/TextCapitalized";
import { TripOrPlanningOptionsDisplay } from "./DayTripDetails";
import { colors } from "../../../styles/figmaColors";
import { Badge } from "../Tag/Badge";
import { getBadgeColorByScore } from "../../getBadgeColorByScore";
import { Avatar } from "../Avatar/Avatar";
import { ButtonDS } from "../Button";

export interface DrivingEventBadgeProps {
  eventType: "smoothnessScore" | "supSpeedScore";
  score: number;
}

export interface TripOrPlanningCardProps {
  dataDisplay: TripOrPlanningOptionsDisplay;
  type: "TRIP" | "PLANNING";
  headerProps?: ReactNode;
  onRequestAddresses?: () => void;
  isPollingAddresses?: boolean;
}

const formatHourAndMinutes = (value: number): string => {
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return `${hours}h${minutes < 10 ? "0" + minutes : minutes}`;
};

const tripDetailsOptionsDict = [
  "distance",
  "duration",
  "startDate",
  "endDate",
  "ecoDrivingScore",
];

const displayTripsDetailsByOptions = (
  option: (typeof tripDetailsOptionsDict)[number],
  detailsValue: string | number,
) => {
  switch (option) {
    case "distance":
    case "ecoDrivingScore":
      return Number(detailsValue).toFixed(1).toString();
    case "duration":
      return formatHourAndMinutes(detailsValue as number);
    case "startDate":
    case "endDate":
      return dateFormatterHourMinute(
        new Date(detailsValue as string),
      ).replaceAll(":", "h");
    default:
      return detailsValue.toString();
  }
};

// const BASE_DRIVING_EVENT_PER_MINUTE = 0.15;

// const DrivingEventBadge = (props: DrivingEventBadgeProps) => {
//   const { t } = useTranslation();

//   return (
//     <StyledFlex>
//       <Badge
//         text={
//           <StyledFlex>
//             <TextCapitalized>
//               {t(`trips.details.drivingEvents.${props.eventType}`)}
//             </TextCapitalized>
//           </StyledFlex>
//         }
//         backgroundColor={getBadgeColorByScore(props.eventType, props.score)}
//         textColor={colors["colors/text/black"]}
//         typo={typographies["Body/XS"]}
//         tooltip={
//           <TooltipDS
//             id={props.eventType}
//             place={"bottom"}
//             content={t(
//               `trips.details.drivingEvents.${props.eventType}.tooltip`,
//             )}
//           />
//         }
//       />
//     </StyledFlex>
//   );
// };

export const TripOrPlanningCard = (props: TripOrPlanningCardProps) => {
  const { t } = useTranslation();
  const { dataDisplay } = props;
  const { driver } = dataDisplay;

  // const drivingEventsPerMinute =
  //   ((dataDisplay.harshAccelerationsCount ?? 0) +
  //     (dataDisplay.harshBrakesCount ?? 0) +
  //     (dataDisplay.sharpTurnsCount ?? 0)) /
  //   ((new Date(dataDisplay.endDate).getTime() -
  //     new Date(dataDisplay.startDate).getTime()) /
  //     60000);

  // const hasBadSmoothnessScore =
  //   drivingEventsPerMinute > BASE_DRIVING_EVENT_PER_MINUTE;
  // const displayDrivingEvents = hasBadSmoothnessScore;

  const [firstName, lastName] = driver ? driver.split(" ") : ["", ""];

  const hasAddresses = useMemo(
    () =>
      Boolean(dataDisplay.departureAddress || dataDisplay.destinationAddress),
    [dataDisplay.departureAddress, dataDisplay.destinationAddress],
  );

  const canRequestAddresses = useMemo(
    () => !hasAddresses && Boolean(props.onRequestAddresses),
    [hasAddresses, props.onRequestAddresses],
  );

  return (
    <StyledTripContainer>
      <Spacer x={1.5} />
      <StyledColumn>
        {props.headerProps && (
          <>
            <Spacer y={1.5} />
            {props.headerProps}
          </>
        )}
        {driver && (
          <>
            <Spacer y={1.5} />
            <StyledRowCenter>
              <TextCapitalized>
                <Avatar
                  size="M"
                  color="mint"
                  firstName={firstName}
                  lastName={lastName}
                />
              </TextCapitalized>
              <Spacer x={0.5} />
              {firstName} {lastName}
            </StyledRowCenter>
          </>
        )}
        {props.type === "TRIP" && (
          <>
            <Spacer y={1.5} />
            <StyledAddresses>
              <StyledTripDetail>
                <StyledOptionTitle>
                  <TextCapitalized>
                    {t(`trips.details.departureAddress.title`)}
                  </TextCapitalized>
                </StyledOptionTitle>
                <Spacer y={0.25} />
                <StyledAddress>
                  {dataDisplay.departureAddress &&
                    getAddressLines(dataDisplay.departureAddress).map(
                      (line, index) => (
                        <div key={`departure-${index}-${line}`}>{line}</div>
                      ),
                    )}
                </StyledAddress>
              </StyledTripDetail>
              <StyledTripDetail>
                <StyledOptionTitle>
                  <TextCapitalized>
                    {t(`trips.details.destinationAddress.title`)}
                  </TextCapitalized>
                </StyledOptionTitle>
                <Spacer y={0.25} />
                <StyledAddress>
                  {dataDisplay.destinationAddress &&
                    getAddressLines(dataDisplay.destinationAddress).map(
                      (line, index) => (
                        <div key={`destination-${index}-${line}`}>{line}</div>
                      ),
                    )}
                </StyledAddress>
              </StyledTripDetail>
            </StyledAddresses>
            {canRequestAddresses && (
              <>
                <Spacer y={0.75} />
                <StyledRequestAddresses>
                  <ButtonDS
                    format="hug"
                    buttonType="secondary"
                    sizeButton="S"
                    disabled={props.isPollingAddresses}
                    text={t(
                      props.isPollingAddresses
                        ? `trips.details.requestAddresses.polling`
                        : `trips.details.requestAddresses.title`,
                    )}
                    onClick={props.onRequestAddresses}
                  />
                </StyledRequestAddresses>
              </>
            )}
          </>
        )}
        <Spacer y={1.5} />
        <StyledBadgesRow>
          {tripDetailsOptionsDict.map((option, index) => {
            const tripDetailsValue = dataDisplay[
              option as keyof TripOrPlanningOptionsDisplay
            ] as string | number;

            const hasDataDisplayValue =
              dataDisplay[option as keyof TripOrPlanningOptionsDisplay] !==
                null &&
              dataDisplay[option as keyof TripOrPlanningOptionsDisplay] !==
                undefined;

            if (!hasDataDisplayValue) return null;

            return (
              <StyledTripDetail key={index + "trip"}>
                <StyledOptionTitle>
                  <TextCapitalized>
                    {t(`trips.details.${option}.title`)}
                  </TextCapitalized>
                </StyledOptionTitle>
                <Spacer y={0.5} />
                <Badge
                  text={
                    <StyledFlex>
                      <TextCapitalized>
                        {displayTripsDetailsByOptions(option, tripDetailsValue)}
                      </TextCapitalized>
                      {t(`trips.details.${option}.units`) !== "" && (
                        <>
                          <Spacer x={0.25} />
                          {t(`trips.details.${option}.units`)}
                        </>
                      )}
                    </StyledFlex>
                  }
                  backgroundColor={getBadgeColorByScore(
                    option,
                    tripDetailsValue,
                  )}
                  textColor={colors["colors/text/black"]}
                  typo={typographies["Body/XS"]}
                />
              </StyledTripDetail>
            );
          })}
          {/* {displayDrivingEvents && (
            <>
              <StyledTripDetail>
                <StyledOptionTitle>
                  <TextCapitalized>
                    {t(`trips.details.drivingEvents.title`)}
                  </TextCapitalized>
                </StyledOptionTitle>
                <Spacer y={0.5} />
                <StyledFlex>
                  {hasBadSmoothnessScore && (
                    <DrivingEventBadge
                      eventType="smoothnessScore"
                      score={
                        (1 -
                          Math.max(
                            drivingEventsPerMinute -
                              BASE_DRIVING_EVENT_PER_MINUTE,
                            0,
                          )) *
                        100
                      }
                    />
                  )}
                </StyledFlex>
              </StyledTripDetail>
            </>
          )} */}
        </StyledBadgesRow>
        <Spacer y={1.5} />
        {dataDisplay.isOutIntervention && props.type === "PLANNING" ? (
          <StyledFlex>
            <StyledTripDetail>
              <StyledOptionTitle>
                <TextCapitalized>
                  {t(`trips.details.isOutIntervention.title`)}
                </TextCapitalized>
              </StyledOptionTitle>
              <Spacer y={0.5} />
              <StyledFlexClientName>
                <Badge
                  text={t(`trips.details.isOutIntervention.value`)}
                  backgroundColor={
                    colors["colors/system/informative/informative_veryLight"]
                  }
                  textColor={colors["colors/text/black"]}
                  typo={typographies["Body/XS"]}
                />
              </StyledFlexClientName>
            </StyledTripDetail>
            <Spacer x={1.5} />
            <StyledTripDetail>
              <StyledOptionTitle>
                <TextCapitalized>
                  {t(`trips.details.activityType.title`)}
                </TextCapitalized>
              </StyledOptionTitle>
              <Spacer y={0.5} />
              <StyledFlexClientName>
                <TextCapitalized>
                  {dataDisplay.activityType ?? "-"}
                </TextCapitalized>
              </StyledFlexClientName>
            </StyledTripDetail>
          </StyledFlex>
        ) : (
          <StyledFlex>
            {dataDisplay.clientName && (
              <>
                <StyledFlex>
                  <StyledTripDetail>
                    <StyledOptionTitle>
                      <TextCapitalized>
                        {t(`trips.details.clientName.title`)}
                      </TextCapitalized>
                    </StyledOptionTitle>
                    <Spacer y={0.5} />
                    <StyledFlexClientName>
                      {dataDisplay.clientName}
                      <Spacer x={1.5} />
                    </StyledFlexClientName>
                  </StyledTripDetail>
                </StyledFlex>
              </>
            )}
            {dataDisplay.clientCity && (
              <>
                <StyledFlex>
                  <StyledTripDetail>
                    <StyledOptionTitle>
                      <TextCapitalized>
                        {t(`trips.details.clientCity.title`)}
                      </TextCapitalized>
                    </StyledOptionTitle>
                    <Spacer y={0.5} />
                    <StyledFlexClientName>
                      {dataDisplay.clientCity}
                    </StyledFlexClientName>
                  </StyledTripDetail>
                </StyledFlex>
              </>
            )}
            {dataDisplay.hasKmClient && (
              <StyledFlex>
                <Spacer x={1.5} />
                <StyledTripDetail>
                  <StyledOptionTitle>
                    <TextCapitalized>
                      {t(`trips.details.hasKmClient.title`)}
                    </TextCapitalized>
                  </StyledOptionTitle>
                  <Spacer y={0.5} />
                  <StyledFlexClientName>
                    <Badge
                      text={t(`trips.details.hasKmClient.value`)}
                      backgroundColor={
                        colors[
                          "colors/system/informative/informative_veryLight"
                        ]
                      }
                      textColor={colors["colors/text/black"]}
                      typo={typographies["Body/XS"]}
                    />
                  </StyledFlexClientName>
                </StyledTripDetail>
              </StyledFlex>
            )}
          </StyledFlex>
        )}
        <Spacer y={1.5} />
      </StyledColumn>
      <Spacer x={1.5} />
    </StyledTripContainer>
  );
};

const StyledTripContainer = styled.div`
  display: flex;
  background-color: ${colors["colors/button/secondary/hover"]};
  border-radius: 0.5rem;
  white-space: nowrap;
`;

const StyledRequestAddresses = styled.div`
  display: flex;
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledBadgesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  align-items: flex-start;
`;

const StyledTripDetail = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledOptionTitle = styled.div`
  color: ${colors["colors/text/black"]};
`;

const StyledRowCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledFlexClientName = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const StyledAddresses = styled.div`
  display: flex;
  gap: 2rem;
`;

const StyledAddress = styled.div`
  ${typographies["Body/M"]}
  display: flex;
  flex-direction: column;
`;
